/** @format */

import React, { useState, useEffect } from "react";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";
import { useForm } from "react-hook-form";
import Switch from "react-switch";
import useBuyStore from "../store/BuyStore";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import BuyStep from "../enums/BuyStep";
import { handleNext } from "../../../shared/utils/navigationUtils";
import TbEPaymentsMapper from "../mappers/tbEPaymentsMapper";
import { useNavigate } from "fusion-plugin-react-router";
import { fetchPaymentJson } from "../services/buyService";
import Loader from "../../../shared/components/Loader";
import PaymentsAccordion from "../../../shared/components/paymentsAccordion";
import {
  TAXI_FINANCE_INTEREST,
  TAXI_FINANCE_APR,
  TAXI_QUOTE_BRANDREF,
} from "../../../config/config";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useQuoteStore from "../../quote/store/QuoteStore";

const TbEPayment = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const buyStore = useBuyStore.getState();
  const navigate = useNavigate();
  const brandRef = TAXI_QUOTE_BRANDREF;
  // Initialize model
  const tbEPaymentsData = TbEPaymentsMapper.get();

  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: tbEPaymentsData,
  });

  let STATE = useAppStateStore();

  const tbEPayments = assetUrl("../../../styles/payments.css");

  const handleSelectOption = (option) => {
    if (option === selectedOption) {
      setSelectedOption(null);
      setValue("selectedOption", null);
      return;
    }

    if (
      (option === "monthly" && !tbEPaymentsData.paymentOptionsMonthly) ||
      (option === "annual" && !tbEPaymentsData.paymentOptionsAnnually)
    ) {
      return;
    }
    setSelectedOption(option);
    setValue("selectedOption", option);
  };

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBEPAYMENTS_ENTERED);
  }, []);

  const onSubmit = async (data) => {
    if (!data.selectedOption) return;

    // Set the selected payment option in buyStore
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBEPAYMENTS_NEXT_CLICKED);
    buyStore.setObject(
      "selectedPaymentOption",
      data.selectedOption === "monthly"
        ? tbEPaymentsData.paymentOptionsMonthly
        : tbEPaymentsData.paymentOptionsAnnually
    );

    setPaymentLoading(true);
    try {
      const selectedPaymentOption = buyStore.getObject("selectedPaymentOption");
      const riskObjectId =
        useQuoteStore.getState().quote?.risk?.riskObjectId ?? "";

      const requestBody = {
        brandRef: brandRef,
        RiskObjectId: riskObjectId,
        RiskObjectHistoryId: 1,
        CardPaymentAmount:
          selectedPaymentOption?.deposit ||
          selectedPaymentOption?.totalAmountToPayByCustomer ||
          5000,
        SelectedPaymentPlanId: selectedPaymentOption?.paymentOptionId,
      };

      const jsonData = await fetchPaymentJson(requestBody, navigate);
      setValue("jsonPaymentData", jsonData);
      buyStore.setObject("jsonPaymentData", jsonData);
      setPaymentLoading(false);
      if (data.selectedOption === "annual") {
        handleNext(BuyStep.TB_GLOBAL_PAYMENTS_PAGE, STATE);
      } else if (data.selectedOption === "monthly") {
        handleNext(BuyStep.TB_DIRECT_DEBIT_DETAILS, STATE);
      }
    } catch (error) {
      console.error("Error fetching payment JSON:", error);
      setPaymentLoading(false);
    }
  };

  if (paymentLoading) {
    return <Loader text="Selecting your payment option..." />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <link rel="stylesheet" href={tbEPayments} />
      </Helmet>

      <QuoteFormWrapper>
        <>
          <div className="payment-container">
            <h2 className="payment-title">Price Breakdown</h2>

            <div className="breakdown-container form4">
              {/* Gross Breakdown */}
              <PaymentsAccordion
                title="Policy"
                headers={["Premium (Excl. IPT)", "IPT", "Premium (Incl. IPT)"]}
                data={[
                  `£
                    ${tbEPaymentsData.quoteBreakdown?.insurerGrossExcIPT.toLocaleString(
                      "en-GB",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}`,
                  `£
                    ${tbEPaymentsData.quoteBreakdown?.insurerIPT.toLocaleString(
                      "en-GB",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                    `,
                  `£
                    ${tbEPaymentsData.quoteBreakdown?.insurerGrossIncIPT.toLocaleString(
                      "en-GB",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                    `,
                ]}
              />

              {/* Add-Ons Section */}
              {Array.isArray(tbEPaymentsData.quoteBreakdown?.addonsQuoted) &&
                tbEPaymentsData.quoteBreakdown.addonsQuoted.length > 0 && (
                  <PaymentsAccordion
                    title="Add-Ons (Incl. IPT)"
                    headers={tbEPaymentsData.quoteBreakdown.addonsQuoted.map(
                      (addon) => `${addon.addonSchemeName}`
                    )}
                    data={tbEPaymentsData.quoteBreakdown.addonsQuoted.map(
                      (addon) =>
                        `£${addon.addonGrossPremiumIncIPT.toLocaleString(
                          "en-GB",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                    )}
                  />
                )}

              {/* Admin Fee Section */}
              <PaymentsAccordion
                title="Our Costs"
                headers={["Admin Fee"]}
                data={[
                  `£${tbEPaymentsData.quoteBreakdown?.adminFee.toLocaleString(
                    "en-GB",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}`,
                ]}
              />

              {/* Total Section */}
              <div
                className="payments_gap-15 accordion-padding"
                style={{
                  background: "#eee",
                  color: "black",
                  marginTop: "15px",
                  borderRadius: "5px",
                }}
              >
                <span className="breakdown-label">
                  <strong>Total</strong>
                </span>
                <span className="breakdown-value">
                  <strong>
                    £
                    {tbEPaymentsData.quoteBreakdown?.quotedTotal.toLocaleString(
                      "en-GB",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </strong>
                </span>
              </div>
            </div>
          </div>

          <div className="payment-container">
            <h2 className="payment-title">Select your payment option</h2>
            <p className="payment-description">
              You're nearly there! Select whether you would like to pay monthly
              or annually for your cover.
            </p>

            {tbEPaymentsData.financeAvailable && (
              <p className="payment-description">
                Paying monthly will cost you £{tbEPaymentsData.serviceCharge}{" "}
                more than paying annually.
              </p>
            )}

            <p className="payment-description">
              Your monthly instalments include a {TAXI_FINANCE_INTEREST}%
              interest charge which is equal to an APR of {TAXI_FINANCE_APR}%.
            </p>

            {/* Monthly Payment Option */}
            {tbEPaymentsData.financeAvailable && (
              <div
                className={`payment-option ${
                  selectedOption === "monthly" ? "selected" : ""
                }`}
                onClick={() => handleSelectOption("monthly")}
              >
                {tbEPaymentsData.paymentOptionsMonthly?.installmentAmount ? (
                  <>
                    <div className="payment-price-div">
                      <div>
                        <h3 className="payment-price lh-0 margin-bottom-0">
                          £
                          {
                            tbEPaymentsData.paymentOptionsMonthly
                              .installmentAmount
                          }
                        </h3>
                      </div>

                      <Switch
                        width={50}
                        height={30}
                        handleDiameter={26}
                        offColor="#e8e8e8"
                        onColor="#175bcc"
                        checkedIcon={false}
                        uncheckedIcon={false}
                        checked={selectedOption === "monthly"}
                        onChange={() => handleSelectOption("monthly")}
                      />
                    </div>

                    <p className="payment-plan">Pay Monthly</p>
                    <p className="payment-detail">
                      <strong>
                        £{tbEPaymentsData.paymentOptionsMonthly.deposit}
                      </strong>{" "}
                      deposit followed by{" "}
                      <span className="installStyle">
                        {tbEPaymentsData.paymentOptionsMonthly.instalments}
                      </span>{" "}
                      installments of{" "}
                      <strong>
                        £
                        {
                          tbEPaymentsData.paymentOptionsMonthly
                            .installmentAmount
                        }
                      </strong>{" "}
                      per month
                    </p>
                  </>
                ) : (
                  <h4>
                    Couldn't get your monthly payments plan.. Please try again
                    later.
                  </h4>
                )}
              </div>
            )}

            {/* Annual Payment Option */}
            <div
              className={`payment-option ${
                selectedOption === "annual" ? "selected" : ""
              }`}
              onClick={() => handleSelectOption("annual")}
            >
              {tbEPaymentsData.paymentOptionsAnnually
                ?.totalAmountToPayByCustomer ? (
                <>
                  <div className="payment-price-div">
                    <h3 className="payment-price lh-0 margin-bottom-10">
                      £
                      {tbEPaymentsData.paymentOptionsAnnually.totalAmountToPayByCustomer.toLocaleString(
                        "en-GB",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )}
                    </h3>
                    <Switch
                      width={50}
                      height={30}
                      handleDiameter={26}
                      offColor="#e8e8e8"
                      onColor="#175bcc"
                      checkedIcon={false}
                      uncheckedIcon={false}
                      checked={selectedOption === "annual"}
                      onChange={() => handleSelectOption("annual")}
                    />
                  </div>

                  <p className="payment-plan">Pay Annually</p>
                  <p className="payment-details2">One off payment</p>
                </>
              ) : (
                <h4>
                  Couldn't get your yearly payments plan.. Please try again
                  later.
                </h4>
              )}
            </div>
          </div>
        </>
      </QuoteFormWrapper>

      <FormNavigationButtons
        prevStep={BuyStep.TB_DOCUMENTS}
        isValid={selectedOption}
      />
    </form>
  );
};

export default TbEPayment;
